import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { of, ReplaySubject } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { ButtonClickDirective } from 'src/app/directives/buttons/button-click-action/button-click.directive';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { ITab } from 'src/app/model/tab';
import { IThingie } from 'src/app/model/thingie/thingie';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { GraphConfigurationComponent, IGraphConfigurationComponentInputData } from '../../graph-configuration-components/graph-configuration/graph-configuration.component';
import { DialogControl } from '../dialog/dialog-control';
import { IIsDialog } from '../dialog/dialog.service';


export interface IGraphConfigDialogArgs {
  readonly tabs: ITab[];

  /**
   * The Thingie for which graph views
   * are to be configured
   */
  readonly thingie: IThingie;

  /**
   * Index of the tab, that shall
   * be selected after the dialog has been opened.
   *
   * If not provided, the first tab will be selected.
   *
   * If the tab index is greater than the number of tabs,
   * the last tab in the list will be selected.
   */
  selectedTabIndex?: number;

  /**
   * If true, create a new tab after opening the dialog
   */
  createNewTab: boolean;

  /**
   * If true, this edits an experiment group graph
   */
  isDashboardCard: boolean;
}


/**
 * Type of the data returned from the dialog, if not `undefined`.
 */
export interface IGraphConfigDialogResult {
  tabs: ITab[];
  selectedTabIndex?: number;
}


@Component({
  imports: [
    MaterialImportsModule,
    GraphConfigurationComponent,
    AsyncPipe,
    ButtonClickDirective
  ],
  templateUrl: './graph-configuration-dialog.component.html'
})
export class GraphConfigurationDialogComponent implements AfterViewInit, IIsDialog<IGraphConfigDialogArgs, IGraphConfigDialogResult> {
  public readonly __compileTimeTypeInferenceHelper: [IGraphConfigDialogArgs, IGraphConfigDialogResult][] = [];

  @ViewChild('buttonTemplate', { read: TemplateRef })
  private set buttonTemplate(value: TemplateRef<unknown>) {
    this.dialogControl.setActionButtonsTemplate(value);
  }


  /**
   * @private
   */
  @ViewChild(GraphConfigurationComponent, { static: true })
  private _graphConfigurationComponent!: GraphConfigurationComponent;
  private _graphConfigurationComponent$ = new ReplaySubject<GraphConfigurationComponent>(1);

  private dialogControl: DialogControl<IGraphConfigDialogArgs, IGraphConfigDialogResult> = inject(DialogControl);


  private dialogInputData = this.dialogControl.data;

  /**
   * State of the validation of the underlying graph configuration component.
   *
   * Enables/disables the save-button
   *
   * @private
   */
  protected validationErrors = this._graphConfigurationComponent$.pipe(
    switchMap(_graphConfigurationComponent => _graphConfigurationComponent.validationErrors),
    delay(0)
  );


  /**
   * Data passed to the graph configuration component
   *
   * Modifications are made to a copy only, so there are not unintentional
   * changes to the original data which would stay after the dialog is closed
   * without saving the changes deliberately.
   *
   * @private
   */
  protected graphConfigData: IGraphConfigurationComponentInputData = deepCopySync(
    this.dialogInputData
  );


  public readonly title$ = of('Graph configuration');


  public ngAfterViewInit(): void {
    this._graphConfigurationComponent$.next(this._graphConfigurationComponent);
  }


  /**
   * @private
   */
  protected async _onSave() {
    await this._graphConfigurationComponent.persistTabConfigurationToUserStorage();
    await this._graphConfigurationComponent.deleteTabsMarkedForDeletion();

    // Get the actual modified tabs and selected index from the component data, not the initial copy
    // This ensures newly added tabs and their selection state are correct
    const tabs = this._graphConfigurationComponent.data.tabs;
    const selectedTabIndex = this._graphConfigurationComponent.data.currentSelectedTabIndex ?? this._graphConfigurationComponent.getSelectedTabIndex() ?? 0;

    // Reset tabs' configs, layouts and data - they might be outdated and will
    // be regenerated soon anyway.
    // Also, during cloning functions like callbacks for modebar buttons
    // etc. were lost, which would be problematic.

    tabs.forEach(tab => {
      tab.plotlyConfig = undefined;
      tab.layout = undefined;
      tab.outputData = [];
    });

    await this.dialogControl.requestCloseDialog({ tabs, selectedTabIndex });
  }


  protected closeDialog() {
    void this.dialogControl.requestCloseDialog(undefined);
  }
}
